/**
 * Placeholder for Clinical Review version history (US 18610).
 *
 * Mirrors the Organization API's `ClinicalReviewVersionHistory/GetAll` response field for
 * field, so switching between the two is a flag rather than a rewrite. Kept because the
 * `none` scenario — a referral whose review has never been submitted — is otherwise hard
 * to reproduce on a populated environment.
 *
 * ── Reverting ─────────────────────────────────────────────────────────────────
 * 1. Set `USE_CLINICAL_REVIEW_VERSION_FIXTURE` to true to render from this file instead.
 * 2. To remove entirely, delete this file and the guarded branches in `api/index.tsx`.
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * All names and content are synthetic. No real patient data belongs in this file.
 */

import {
  ClinicalReviewVersion,
  ClinicalReviewVersionsResponse,
} from '../types/clinicalReviewVersion'

/** Master switch. False routes to the real API. */
export const USE_CLINICAL_REVIEW_VERSION_FIXTURE = false

/**
 * Which state to render.
 *
 * `none` matters as much as `populated` — the story requires that a first-time visit to
 * Clinical Review shows no indicator and no history at all.
 */
type FixtureScenario = 'populated' | 'none'

// Asserted rather than annotated: TypeScript narrows an annotated const to its
// initializer, which would make the other branch unreachable.
const FIXTURE_SCENARIO = 'populated' as FixtureScenario

const FIXTURE_DELAY_MS = 300

/** UTC without a Z suffix, as the API sends it. */
const daysAgo = (days: number, atHour: number, atMinute: number): string => {
  const date = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
  date.setHours(atHour, atMinute, 0, 0)
  return date.toISOString().replace('Z', '')
}

/** Newest first, as the backend orders them. */
const VERSIONS: ClinicalReviewVersion[] = [
  {
    id: 9003,
    patientId: 0,
    version: 'Version 3',
    reason: 'Reverted to Clinical Review',
    clinicalReviewPdf: 'clinical-review-9003.pdf',
    createdDate: daysAgo(2, 10, 42),
    createdUser: 'Amanda Rios',
  },
  {
    id: 9002,
    patientId: 0,
    version: 'Version 2',
    reason: 'Clinical notes updated',
    clinicalReviewPdf: 'clinical-review-9002.pdf',
    createdDate: daysAgo(6, 15, 12),
    createdUser: 'Amanda Rios',
  },
  {
    id: 9001,
    patientId: 0,
    version: 'Version 1',
    reason: '',
    clinicalReviewPdf: '',
    createdDate: daysAgo(9, 9, 4),
    createdUser: 'Robert Fields',
  },
]

const delay = <T>(value: T): Promise<T> =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), FIXTURE_DELAY_MS)
  })

/** Stands in for `POST Organization/api/ClinicalReviewVersionHistory/GetAll`. */
export const getClinicalReviewVersionsFixture = (): Promise<ClinicalReviewVersionsResponse> => {
  const versions = FIXTURE_SCENARIO === 'none' ? null : VERSIONS

  return delay({
    data: {
      isSuccess: true,
      message: 'success',
      data: versions,
      totalCount: versions?.length ?? 0,
      hasData: Boolean(versions?.length),
    },
  })
}

/** True while the fixture supplies data, so callers can skip real downloads. */
export const clinicalReviewVersionPlaceholder = {
  enabled: USE_CLINICAL_REVIEW_VERSION_FIXTURE,
}
